/**
 * EditorScene - Phaser scene for the locale editor canvas
 * 
 * Handles:
 * - Rendering props, positions, and walls
 * - Drag & drop interactions
 * - Selection management
 * - Zoom and pan
 * - Wall drawing mode
 */

import Phaser from 'phaser';
import * as jsyaml from 'js-yaml';
import type { PropData, Position, Wall, LocaleData, PropRegistryEntry } from './types';
import { EditorState } from './EditorState';

// ============================================
// CONTEXT INTERFACE
// ============================================

/**
 * EditorContext provides access to shared state and callbacks.
 * This decouples EditorScene from editor-main.ts module-level variables.
 */
export interface EditorContext {
  // State object
  state: EditorState;
  
  // Prop registry (loaded from props.json)
  getPropRegistry: () => PropRegistryEntry[];
  
  // Selection state
  getSelectedProp: () => PropData | null;
  setSelectedProp: (prop: PropData | null) => void;
  getSelectedPosition: () => string | null;
  setSelectedPosition: (name: string | null) => void;
  getSelectedWall: () => number | null;
  setSelectedWall: (index: number | null) => void;
  
  // Visibility flags
  getShowProps: () => boolean;
  setShowProps: (show: boolean) => void;
  getShowWalls: () => boolean;
  setShowWalls: (show: boolean) => void;
  getShowSpawnPoints: () => boolean;
  setShowSpawnPoints: (show: boolean) => void;
  
  // Zoom state
  getCurrentZoom: () => number;
  setCurrentZoom: (zoom: number) => void;
  
  // Interaction state
  getIsDragging: () => boolean;
  setIsDragging: (dragging: boolean) => void;
  getIsPanning: () => boolean;
  setIsPanning: (panning: boolean) => void;
  getPanStart: () => { x: number; y: number };
  setPanStart: (x: number, y: number) => void;
  getCameraStart: () => { x: number; y: number };
  setCameraStart: (x: number, y: number) => void;
  
  // Wall drawing state
  getWallDrawMode: () => boolean;
  setWallDrawMode: (mode: boolean) => void;
  getIsDrawingWall: () => boolean;
  setIsDrawingWall: (drawing: boolean) => void;
  getWallStartPoint: () => { x: number; y: number } | null;
  setWallStartPoint: (point: { x: number; y: number } | null) => void;
  getWallPreviewLine: () => Phaser.GameObjects.Graphics | null;
  setWallPreviewLine: (line: Phaser.GameObjects.Graphics | null) => void;
  getWallGraphics: () => Phaser.GameObjects.Graphics | null;
  setWallGraphics: (graphics: Phaser.GameObjects.Graphics | null) => void;
  
  // Locale state
  getCurrentLocaleName: () => string;
  setCurrentLocaleName: (name: string) => void;
  getCurrentLocaleData: () => LocaleData | null;
  setCurrentLocaleData: (data: LocaleData | null) => void;
  
  // Position markers (Phaser objects)
  getSpawnPointMarkers: () => Phaser.GameObjects.Container[];
  setSpawnPointMarkers: (markers: Phaser.GameObjects.Container[]) => void;
  
  // Callbacks
  onSaveState: (reason: string) => void;
  onUpdatePropertyPanel: () => void;
  onUpdatePropCount: () => void;
  onUpdateZoomDisplay: () => void;
  onUpdateSaveButtonState: (localeName: string) => void;
  onResetZoom: () => void;
  onUpdateUndoRedoButtons: () => void;
  onSetUnsavedChanges: (unsaved: boolean) => void;
  onShowPositionProperties: (name: string, pos: Position) => void;
  onHidePositionProperties: () => void;
  onSetPositionsDirty: (dirty: boolean) => void;
  onClearUndoRedo: () => void;
  onGetIsUndoRedoAction: () => boolean;
  onLocaleLoaded: (localeData: LocaleData | null) => void;
  
  // Scene reference setter (called in create())
  onSceneReady: (scene: EditorScene) => void;
}

// ============================================
// CONSTANTS
// ============================================

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.1;
const DEFAULT_WALL_COLOR = 0x555555;
const DEFAULT_WALL_THICKNESS = 8;

// ============================================
// EDITOR SCENE CLASS
// ============================================

export class EditorScene extends Phaser.Scene {
  // Scene-specific state
  propSprites: Map<number, Phaser.GameObjects.Image> = new Map();
  selectionRect: Phaser.GameObjects.Rectangle | null = null;
  currentLocalePositions: Record<string, Position> = {};
  bgGraphics: Phaser.GameObjects.Graphics | null = null;
  spaceKey: Phaser.Input.Keyboard.Key | null = null;
  worldScale = 1;
  worldWidth = 800;
  worldHeight = 450;
  
  // Context reference (set via static property before scene creation)
  private ctx!: EditorContext;
  
  // Static context holder (Phaser doesn't allow constructor params for scenes)
  private static _context: EditorContext | null = null;
  
  static setContext(context: EditorContext): void {
    EditorScene._context = context;
  }
  
  constructor() {
    super({ key: 'EditorScene' });
  }
  
  init(): void {
    if (!EditorScene._context) {
      throw new Error('EditorScene context not set. Call EditorScene.setContext() before creating the game.');
    }
    this.ctx = EditorScene._context;
  }
  
  preload(): void {
    for (const prop of this.ctx.getPropRegistry()) {
      this.load.image(prop.key, prop.path);
    }
  }
  
  create(): void {
    // Notify main module that scene is ready
    this.ctx.onSceneReady(this);
    
    this.drawBackground();
    
    this.selectionRect = this.add.rectangle(0, 0, 0, 0, 0xe94560, 0.3);
    this.selectionRect.setStrokeStyle(2, 0xe94560);
    this.selectionRect.setVisible(false);
    this.selectionRect.setDepth(10000);
    
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on('pointerup', this.onPointerUp, this);
    this.input.on('wheel', this.onMouseWheel, this);
    
    if (this.input.keyboard) {
      this.spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
      
      this.input.keyboard.on('keydown-PLUS', () => this.zoomIn());
      this.input.keyboard.on('keydown-MINUS', () => this.zoomOut());
      this.input.keyboard.on('keydown-NUMPAD_ADD', () => this.zoomIn());
      this.input.keyboard.on('keydown-NUMPAD_SUBTRACT', () => this.zoomOut());
      this.input.keyboard.on('keydown-ZERO', () => this.ctx.onResetZoom());
      this.input.keyboard.on('keydown-NUMPAD_ZERO', () => this.ctx.onResetZoom());
      
      this.input.keyboard.on('keydown-LEFT', () => this.nudgeProp(-1, 0));
      this.input.keyboard.on('keydown-RIGHT', () => this.nudgeProp(1, 0));
      this.input.keyboard.on('keydown-UP', () => this.nudgeProp(0, -1));
      this.input.keyboard.on('keydown-DOWN', () => this.nudgeProp(0, 1));
    }
    
    this.drawBackground(null);
    console.log('Editor ready');
  }
  
  drawBackground(localeData: LocaleData | null = null): void {
    if (this.bgGraphics) {
      this.bgGraphics.destroy();
    }
    
    const bg = this.add.graphics();
    this.bgGraphics = bg;
    bg.setDepth(-1000);
    
    const wallColor = localeData?.locale?.wallColor ?? 0x8b7355;
    const floorColor = localeData?.locale?.floorColor ?? 0xdeb887;
    const backgroundColor = localeData?.locale?.background ?? 0x654321;
    
    const worldWidth = localeData?.locale?.world?.width ?? 800;
    const worldHeight = localeData?.locale?.world?.height ?? 450;
    
    const scaleX = 800 / worldWidth;
    const scaleY = 450 / worldHeight;
    const scale = Math.min(scaleX, scaleY);
    
    this.worldScale = scale;
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
    
    this.ctx.setCurrentZoom(scale);
    this.cameras.main.setZoom(this.ctx.getCurrentZoom());
    this.cameras.main.centerOn(worldWidth / 2, worldHeight / 2);
    this.ctx.onUpdateZoomDisplay();
    
    bg.fillStyle(wallColor, 1);
    bg.fillRect(0, 0, worldWidth, 80);
    bg.fillStyle(backgroundColor, 1);
    bg.fillRect(0, 80, worldWidth, 40);
    
    bg.fillStyle(floorColor, 1);
    bg.fillRect(0, 120, worldWidth, worldHeight - 120);
    
    bg.lineStyle(1, 0x000000, 0.1);
    for (let x = 0; x < worldWidth; x += 40) {
      bg.lineBetween(x, 120, x, worldHeight);
    }
    for (let y = 120; y < worldHeight; y += 40) {
      bg.lineBetween(0, y, worldWidth, y);
    }
  }
  
  async loadLocaleProps(localeName: string): Promise<void> {
    this.clearAllProps();
    this.ctx.setCurrentLocaleName(localeName);
    
    let localeProps: Array<{ key: string; x: number; y: number; scale?: number; depth?: number; flipX?: boolean; angle?: number }> = [];
    let localeData: LocaleData | null = null;
    
    try {
      const response = await fetch(`/api/locales/${localeName}`);
      if (response.ok) {
        const yamlText = await response.text();
        localeData = jsyaml.load(yamlText) as LocaleData;
        this.ctx.setCurrentLocaleData(localeData);
        localeProps = localeData?.locale?.props || [];
        this.currentLocalePositions = localeData?.locale?.positions || {};
        this.loadWalls(localeData?.locale?.walls || []);
      }
    } catch (error) {
      console.warn('Failed to fetch locale from API:', error);
      this.ctx.setCurrentLocaleData(null);
      this.loadWalls([]);
    }
    
    this.drawBackground(localeData);
    
    for (const propData of localeProps) {
      this.addProp(propData.key, propData.x, propData.y, propData.scale || 2, propData.depth, propData.flipX, propData.angle || 0, true);
    }
    
    this.ctx.onClearUndoRedo();
    this.ctx.onUpdateUndoRedoButtons();
    this.ctx.onSetUnsavedChanges(false);
    
    this.ctx.onUpdatePropCount();
    this.ctx.onUpdateSaveButtonState(localeName);
    this.renderSpawnPoints(localeName);
    this.ctx.onLocaleLoaded(localeData);
  }
  
  /**
   * Update locale colors and redraw the background
   */
  updateLocaleColors(wallColor: number, floorColor: number, bgColor: number): void {
    const localeData = this.ctx.getCurrentLocaleData();
    if (!localeData?.locale) return;
    
    // Update the locale data
    localeData.locale.wallColor = wallColor;
    localeData.locale.floorColor = floorColor;
    localeData.locale.background = bgColor;
    
    // Redraw background
    this.drawBackground(localeData);
    
    // Mark as dirty
    this.ctx.onSetUnsavedChanges(true);
  }
  
  addProp(key: string, x: number, y: number, scale = 2, depth: number | null | undefined = null, flipX = false, angle = 0, skipSaveState = false): PropData | null {
    if (!this.textures.exists(key)) {
      console.warn(`Texture ${key} not loaded`);
      return null;
    }
    
    if (!skipSaveState && !this.ctx.onGetIsUndoRedoAction()) {
      this.ctx.onSaveState('addProp');
    }
    
    const sprite = this.add.image(x, y, key);
    sprite.setScale(scale);
    sprite.setDepth(depth !== null && depth !== undefined ? depth : y);
    if (this.ctx.getShowProps()) {
      sprite.setInteractive({ draggable: true });
    }
    if (flipX) sprite.setFlipX(true);
    if (angle) sprite.setAngle(angle);
    sprite.setVisible(this.ctx.getShowProps());
    
    const propData: PropData = {
      id: Date.now() + Math.random(),
      key,
      x,
      y,
      scale,
      depth: depth !== null && depth !== undefined ? depth : y,
      flipX,
      angle: angle || 0,
      sprite,
    };
    
    this.ctx.state.addProp(propData);
    this.propSprites.set(propData.id!, sprite);
    
    let dragStartPos: { x: number; y: number } | null = null;
    let hasMoved = false;
    
    sprite.on('dragstart', () => {
      dragStartPos = { x: sprite.x, y: sprite.y };
      hasMoved = false;
    });
    
    sprite.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      const newX = Math.round(dragX);
      const newY = Math.round(dragY);
      
      if (!hasMoved && dragStartPos && (newX !== dragStartPos.x || newY !== dragStartPos.y)) {
        hasMoved = true;
        if (!this.ctx.onGetIsUndoRedoAction()) {
          this.ctx.onSaveState('move');
        }
      }
      
      const oldY = propData.y;
      sprite.x = newX;
      sprite.y = newY;
      propData.x = sprite.x;
      propData.y = sprite.y;
      if (propData.depth === oldY) {
        sprite.setDepth(sprite.y);
        propData.depth = sprite.y;
      }
      this.ctx.onUpdatePropertyPanel();
    });
    
    this.ctx.onUpdatePropCount();
    return propData;
  }
  
  clearAllProps(): void {
    const props = this.ctx.state.props;
    for (const prop of props) {
      prop.sprite.destroy();
    }
    this.ctx.state.clearProps();
    this.propSprites.clear();
    this.ctx.setSelectedProp(null);
    this.ctx.onUpdatePropertyPanel();
    this.ctx.onUpdatePropCount();
  }
  
  deleteSelected(): void {
    const selectedWall = this.ctx.getSelectedWall();
    if (selectedWall !== null && selectedWall >= 0) {
      this.deleteSelectedWall();
      return;
    }
    
    const selectedPosition = this.ctx.getSelectedPosition();
    if (selectedPosition) {
      if (confirm(`Delete position "${selectedPosition}"?`)) {
        this.deletePosition(selectedPosition);
      }
      return;
    }
    
    const selectedProp = this.ctx.getSelectedProp();
    if (!selectedProp) return;
    
    this.ctx.onSaveState('delete');
    
    // Remove from state (also clears selection)
    this.ctx.state.removeProp(selectedProp);
    
    selectedProp.sprite.destroy();
    this.propSprites.delete(selectedProp.id!);
    
    this.ctx.setSelectedProp(null);
    this.ctx.onUpdatePropertyPanel();
    this.ctx.onUpdatePropCount();
  }
  
  renderSpawnPoints(_localeName: string): void {
    this.clearSpawnPoints();
    if (!this.ctx.getShowSpawnPoints()) return;
    
    const spawns = this.currentLocalePositions;
    const selectedPosition = this.ctx.getSelectedPosition();
    const markers: Phaser.GameObjects.Container[] = [];
    
    for (const [name, pos] of Object.entries(spawns)) {
      const container = this.add.container(pos.x, pos.y);
      container.setDepth(10000);
      (container as unknown as { positionName: string }).positionName = name;
      
      const body = this.add.graphics();
      const isSelected = selectedPosition === name;
      
      const fillColor = isSelected ? 0x27ae60 : 0x4a90d9;
      const strokeColor = isSelected ? 0x27ae60 : 0x4a90d9;
      
      body.fillStyle(fillColor, 0.35);
      body.fillEllipse(0, -20, 24, 40);
      body.fillCircle(0, -45, 12);
      body.lineStyle(2, strokeColor, isSelected ? 1 : 0.7);
      body.strokeEllipse(0, -20, 24, 40);
      body.strokeCircle(0, -45, 12);
      
      const labelBg = this.add.graphics();
      const labelWidth = name.length * 7 + 10;
      labelBg.fillStyle(0x000000, 0.7);
      labelBg.fillRoundedRect(-labelWidth/2, 5, labelWidth, 18, 4);
      
      const labelColor = isSelected ? '#27ae60' : '#4a90d9';
      const label = this.add.text(0, 14, name, {
        fontSize: '11px',
        fontFamily: 'monospace',
        color: labelColor,
      });
      label.setOrigin(0.5, 0.5);
      
      container.add([body, labelBg, label]);
      
      const hitZone = this.add.zone(0, -20, 60, 80);
      container.add(hitZone);
      hitZone.setInteractive({ useHandCursor: true, draggable: true });
      
      hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (!pointer.rightButtonDown()) {
          this.ctx.setSelectedPosition(name);
          this.updateSpawnPointSelection();
          this.ctx.onShowPositionProperties(name, this.currentLocalePositions[name]);
        }
      });
      
      hitZone.on('drag', (pointer: Phaser.Input.Pointer) => {
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        container.x = worldPoint.x;
        container.y = worldPoint.y;
      });
      
      hitZone.on('dragend', () => {
        this.currentLocalePositions[name].x = Math.round(container.x);
        this.currentLocalePositions[name].y = Math.round(container.y);
        this.ctx.onSetPositionsDirty(true);
        this.ctx.onSetUnsavedChanges(true);
        this.ctx.onShowPositionProperties(name, this.currentLocalePositions[name]);
      });
      
      markers.push(container);
    }
    
    this.ctx.setSpawnPointMarkers(markers);
  }
  
  updateSpawnPointSelection(): void {
    const markers = this.ctx.getSpawnPointMarkers();
    const selectedPosition = this.ctx.getSelectedPosition();
    
    for (const container of markers) {
      const name = (container as unknown as { positionName: string }).positionName;
      const isSelected = selectedPosition === name;
      
      const body = container.list[0] as Phaser.GameObjects.Graphics;
      if (body) {
        body.clear();
        const fillColor = isSelected ? 0x27ae60 : 0x4a90d9;
        const strokeColor = isSelected ? 0x27ae60 : 0x4a90d9;
        
        body.fillStyle(fillColor, 0.35);
        body.fillEllipse(0, -20, 24, 40);
        body.fillCircle(0, -45, 12);
        body.lineStyle(2, strokeColor, isSelected ? 1 : 0.7);
        body.strokeEllipse(0, -20, 24, 40);
        body.strokeCircle(0, -45, 12);
      }
      
      const label = container.list[2] as Phaser.GameObjects.Text;
      if (label) {
        label.setColor(isSelected ? '#27ae60' : '#4a90d9');
      }
    }
  }
  
  clearSpawnPoints(): void {
    const markers = this.ctx.getSpawnPointMarkers();
    for (const marker of markers) {
      marker.destroy();
    }
    this.ctx.setSpawnPointMarkers([]);
  }
  
  toggleSpawnPoints(): boolean {
    const newValue = !this.ctx.getShowSpawnPoints();
    this.ctx.setShowSpawnPoints(newValue);
    this.renderSpawnPoints(this.ctx.getCurrentLocaleName());
    return newValue;
  }
  
  toggleProps(): boolean {
    const props = this.ctx.state.props;
    const newValue = !this.ctx.getShowProps();
    this.ctx.setShowProps(newValue);
    
    for (const prop of props) {
      prop.sprite.setVisible(newValue);
      if (newValue) {
        prop.sprite.setInteractive({ draggable: true });
      } else {
        prop.sprite.disableInteractive();
      }
    }
    
    if (!newValue && this.ctx.getSelectedProp()) {
      this.ctx.setSelectedProp(null);
      this.selectionRect?.setVisible(false);
      this.ctx.onUpdatePropertyPanel();
    }
    
    return newValue;
  }
  
  addPosition(name: string): boolean {
    if (this.currentLocalePositions[name]) {
      alert(`Position "${name}" already exists!`);
      return false;
    }
    this.currentLocalePositions[name] = {
      x: 400,
      y: 225,
      label: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    };
    this.ctx.onSetPositionsDirty(true);
    this.ctx.onSetUnsavedChanges(true);
    this.ctx.setSelectedPosition(name);
    this.renderSpawnPoints(this.ctx.getCurrentLocaleName());
    this.ctx.onShowPositionProperties(name, this.currentLocalePositions[name]);
    console.log(`Added position "${name}"`);
    return true;
  }
  
  deletePosition(name: string): void {
    delete this.currentLocalePositions[name];
    if (this.ctx.getSelectedPosition() === name) {
      this.ctx.setSelectedPosition(null);
    }
    this.ctx.onSetPositionsDirty(true);
    this.ctx.onSetUnsavedChanges(true);
    this.renderSpawnPoints(this.ctx.getCurrentLocaleName());
    this.ctx.onHidePositionProperties();
    console.log(`Deleted position "${name}"`);
  }
  
  // Wall methods
  initWalls(): void {
    let wallGraphics = this.ctx.getWallGraphics();
    if (!wallGraphics) {
      wallGraphics = this.add.graphics();
      wallGraphics.setDepth(-500);
      this.ctx.setWallGraphics(wallGraphics);
    }
  }
  
  drawWalls(): void {
    const wallGraphics = this.ctx.getWallGraphics();
    if (!wallGraphics) return;
    wallGraphics.clear();
    if (!this.ctx.getShowWalls()) return;
    
    const walls = this.ctx.state.walls;
    const selectedWall = this.ctx.getSelectedWall();
    
    for (let i = 0; i < walls.length; i++) {
      const wall = walls[i];
      const color = wall.color ?? DEFAULT_WALL_COLOR;
      const thickness = wall.thickness ?? DEFAULT_WALL_THICKNESS;
      const isSelected = selectedWall === i;
      
      wallGraphics.lineStyle(thickness, color, 1);
      wallGraphics.lineBetween(wall.x1, wall.y1, wall.x2, wall.y2);
      
      if (isSelected) {
        wallGraphics.lineStyle(2, 0xe94560, 1);
        wallGraphics.lineBetween(wall.x1, wall.y1, wall.x2, wall.y2);
        wallGraphics.fillStyle(0xe94560, 1);
        wallGraphics.fillCircle(wall.x1, wall.y1, 6);
        wallGraphics.fillCircle(wall.x2, wall.y2, 6);
      }
    }
  }
  
  toggleWalls(): boolean {
    const newValue = !this.ctx.getShowWalls();
    this.ctx.setShowWalls(newValue);
    this.drawWalls();
    if (!newValue) {
      this.ctx.setSelectedWall(null);
      this.ctx.setWallDrawMode(false);
    }
    return newValue;
  }
  
  setWallDrawMode(enabled: boolean): void {
    this.ctx.setWallDrawMode(enabled);
    const container = document.getElementById('game-container');
    if (container) {
      container.style.cursor = enabled ? 'crosshair' : 'default';
    }
    const wallPreviewLine = this.ctx.getWallPreviewLine();
    if (!enabled && wallPreviewLine) {
      wallPreviewLine.destroy();
      this.ctx.setWallPreviewLine(null);
    }
  }
  
  loadWalls(wallData: Wall[]): void {
    const walls = this.ctx.state.walls;
    walls.length = 0;
    if (wallData) {
      walls.push(...wallData);
    }
    this.initWalls();
    this.drawWalls();
  }
  
  deleteSelectedWall(): void {
    const selectedWall = this.ctx.getSelectedWall();
    const walls = this.ctx.state.walls;
    if (selectedWall !== null && selectedWall >= 0 && selectedWall < walls.length) {
      walls.splice(selectedWall, 1);
      this.ctx.setSelectedWall(null);
      this.drawWalls();
      this.ctx.onSetPositionsDirty(true);
      this.ctx.onSetUnsavedChanges(true);
    }
  }
  
  // Input handlers
  onPointerDown(pointer: Phaser.Input.Pointer): void {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    
    if (this.spaceKey?.isDown || pointer.middleButtonDown()) {
      this.ctx.setIsPanning(true);
      this.ctx.setPanStart(pointer.x, pointer.y);
      this.ctx.setCameraStart(this.cameras.main.scrollX, this.cameras.main.scrollY);
      return;
    }
    
    if (this.ctx.getWallDrawMode()) {
      this.startWallDraw(worldPoint.x, worldPoint.y);
      return;
    }
    
    if (this.ctx.getShowWalls() && !this.ctx.getWallDrawMode()) {
      const wallIndex = this.findWallAtPoint(worldPoint.x, worldPoint.y);
      if (wallIndex >= 0) {
        this.ctx.setSelectedWall(wallIndex);
        this.ctx.setSelectedProp(null);
        this.ctx.setSelectedPosition(null);
        this.drawWalls();
        this.ctx.onUpdatePropertyPanel();
        return;
      }
    }
    
    const hitSprite = this.findPropAtPoint(worldPoint.x, worldPoint.y);
    const props = this.ctx.state.props;
    
    if (hitSprite) {
      // hitSprite is an Image, but props store Sprite - they're compatible for comparison
      const foundProp = props.find(p => p.sprite === hitSprite) || null;
      this.ctx.setSelectedProp(foundProp);
      this.ctx.setIsDragging(true);
      this.ctx.setSelectedWall(null);
      if (this.ctx.getSelectedPosition()) {
        this.ctx.setSelectedPosition(null);
        this.renderSpawnPoints(this.ctx.getCurrentLocaleName());
      }
    } else {
      this.ctx.setSelectedProp(null);
      this.ctx.setSelectedWall(null);
    }
    
    this.drawWalls();
    this.ctx.onUpdatePropertyPanel();
    this.updateSelectionRect();
  }
  
  onPointerMove(pointer: Phaser.Input.Pointer): void {
    const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const mousePos = document.getElementById('mousePos');
    if (mousePos) {
      mousePos.textContent = `${Math.round(worldPoint.x)}, ${Math.round(worldPoint.y)}`;
    }
    
    if (this.ctx.getIsPanning() && pointer.isDown) {
      const camera = this.cameras.main;
      const panStart = this.ctx.getPanStart();
      const cameraStart = this.ctx.getCameraStart();
      const currentZoom = this.ctx.getCurrentZoom();
      const dx = (pointer.x - panStart.x) / currentZoom;
      const dy = (pointer.y - panStart.y) / currentZoom;
      camera.scrollX = cameraStart.x - dx;
      camera.scrollY = cameraStart.y - dy;
      return;
    }
    
    if (this.ctx.getIsDrawingWall()) {
      this.updateWallPreview(worldPoint.x, worldPoint.y);
    }
  }
  
  screenToWorld(screenX: number, screenY: number): Phaser.Math.Vector2 {
    return this.cameras.main.getWorldPoint(screenX, screenY);
  }
  
  onPointerUp(pointer: Phaser.Input.Pointer): void {
    this.ctx.setIsDragging(false);
    this.ctx.setIsPanning(false);
    
    if (this.ctx.getIsDrawingWall()) {
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      this.finishWallDraw(worldPoint.x, worldPoint.y);
    }
  }
  
  onMouseWheel(_pointer: Phaser.Input.Pointer, _gameObjects: unknown[], _deltaX: number, deltaY: number): void {
    const pointer = this.input.activePointer;
    const camera = this.cameras.main;
    const worldPointBefore = camera.getWorldPoint(pointer.x, pointer.y);
    
    const currentZoom = this.ctx.getCurrentZoom();
    const zoomDelta = deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const newZoom = Phaser.Math.Clamp(currentZoom + zoomDelta, MIN_ZOOM, MAX_ZOOM);
    
    if (newZoom !== currentZoom) {
      this.ctx.setCurrentZoom(newZoom);
      camera.setZoom(newZoom);
      
      const worldPointAfter = camera.getWorldPoint(pointer.x, pointer.y);
      camera.scrollX += worldPointBefore.x - worldPointAfter.x;
      camera.scrollY += worldPointBefore.y - worldPointAfter.y;
      
      this.ctx.onUpdateZoomDisplay();
    }
  }
  
  zoomIn(): void {
    const camera = this.cameras.main;
    const currentZoom = this.ctx.getCurrentZoom();
    const newZoom = Phaser.Math.Clamp(currentZoom + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
    if (newZoom !== currentZoom) {
      this.ctx.setCurrentZoom(newZoom);
      camera.setZoom(newZoom);
      this.ctx.onUpdateZoomDisplay();
    }
  }
  
  zoomOut(): void {
    const camera = this.cameras.main;
    const currentZoom = this.ctx.getCurrentZoom();
    const newZoom = Phaser.Math.Clamp(currentZoom - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
    if (newZoom !== currentZoom) {
      this.ctx.setCurrentZoom(newZoom);
      camera.setZoom(newZoom);
      this.ctx.onUpdateZoomDisplay();
    }
  }
  
  nudgeProp(dx: number, dy: number): void {
    const selectedProp = this.ctx.getSelectedProp();
    if (!selectedProp) return;
    this.ctx.onSaveState('nudge');
    selectedProp.x += dx;
    selectedProp.y += dy;
    selectedProp.sprite.setPosition(selectedProp.x, selectedProp.y);
    this.ctx.onUpdatePropertyPanel();
  }
  
  findPropAtPoint(x: number, y: number): Phaser.GameObjects.Image | null {
    if (!this.ctx.getShowProps()) return null;
    
    const props = this.ctx.state.props;
    let topProp: Phaser.GameObjects.Image | null = null;
    let topDepth = -Infinity;
    
    for (const prop of props) {
      const bounds = prop.sprite.getBounds();
      if (bounds.contains(x, y) && prop.sprite.depth > topDepth) {
        topProp = prop.sprite;
        topDepth = prop.sprite.depth;
      }
    }
    
    return topProp;
  }
  
  findWallAtPoint(worldX: number, worldY: number): number {
    const threshold = 15;
    const walls = this.ctx.state.walls;
    
    for (let i = walls.length - 1; i >= 0; i--) {
      const wall = walls[i];
      const dist = this.pointToLineDistance(worldX, worldY, wall.x1, wall.y1, wall.x2, wall.y2);
      if (dist < threshold) {
        return i;
      }
    }
    return -1;
  }
  
  pointToLineDistance(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    
    if (lenSq !== 0) param = dot / lenSq;
    
    let xx: number, yy: number;
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }
    
    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  startWallDraw(worldX: number, worldY: number): void {
    if (!this.ctx.getWallDrawMode()) return;
    
    this.ctx.setIsDrawingWall(true);
    this.ctx.setWallStartPoint({ x: Math.round(worldX), y: Math.round(worldY) });
    
    let wallPreviewLine = this.ctx.getWallPreviewLine();
    if (wallPreviewLine) wallPreviewLine.destroy();
    wallPreviewLine = this.add.graphics();
    wallPreviewLine.setDepth(10000);
    this.ctx.setWallPreviewLine(wallPreviewLine);
  }
  
  updateWallPreview(worldX: number, worldY: number): void {
    const wallStartPoint = this.ctx.getWallStartPoint();
    const wallPreviewLine = this.ctx.getWallPreviewLine();
    if (!this.ctx.getIsDrawingWall() || !wallStartPoint || !wallPreviewLine) return;
    
    wallPreviewLine.clear();
    
    const dx = Math.abs(worldX - wallStartPoint.x);
    const dy = Math.abs(worldY - wallStartPoint.y);
    
    let endX: number, endY: number;
    if (dx > dy) {
      endX = Math.round(worldX);
      endY = wallStartPoint.y;
    } else {
      endX = wallStartPoint.x;
      endY = Math.round(worldY);
    }
    
    wallPreviewLine.lineStyle(DEFAULT_WALL_THICKNESS, DEFAULT_WALL_COLOR, 0.5);
    wallPreviewLine.lineBetween(wallStartPoint.x, wallStartPoint.y, endX, endY);
    
    wallPreviewLine.fillStyle(0x00ff00, 1);
    wallPreviewLine.fillCircle(wallStartPoint.x, wallStartPoint.y, 4);
    wallPreviewLine.fillCircle(endX, endY, 4);
  }
  
  finishWallDraw(worldX: number, worldY: number): void {
    const wallStartPoint = this.ctx.getWallStartPoint();
    if (!this.ctx.getIsDrawingWall() || !wallStartPoint) return;
    
    const dx = Math.abs(worldX - wallStartPoint.x);
    const dy = Math.abs(worldY - wallStartPoint.y);
    
    let endX: number, endY: number;
    if (dx > dy) {
      endX = Math.round(worldX);
      endY = wallStartPoint.y;
    } else {
      endX = wallStartPoint.x;
      endY = Math.round(worldY);
    }
    
    const length = Math.sqrt(Math.pow(endX - wallStartPoint.x, 2) + Math.pow(endY - wallStartPoint.y, 2));
    if (length > 10) {
      const newWall: Wall = {
        x1: wallStartPoint.x,
        y1: wallStartPoint.y,
        x2: endX,
        y2: endY,
        thickness: DEFAULT_WALL_THICKNESS,
        color: DEFAULT_WALL_COLOR
      };
      const walls = this.ctx.state.walls;
      walls.push(newWall);
      this.ctx.setSelectedWall(walls.length - 1);
      this.drawWalls();
      this.ctx.onSetPositionsDirty(true);
      this.ctx.onSetUnsavedChanges(true);
    }
    
    this.ctx.setIsDrawingWall(false);
    this.ctx.setWallStartPoint(null);
    const wallPreviewLine = this.ctx.getWallPreviewLine();
    if (wallPreviewLine) {
      wallPreviewLine.destroy();
      this.ctx.setWallPreviewLine(null);
    }
  }
  
  updateSelectionRect(): void {
    const selectedProp = this.ctx.getSelectedProp();
    if (selectedProp && selectedProp.sprite && this.selectionRect) {
      const bounds = selectedProp.sprite.getBounds();
      this.selectionRect.setPosition(bounds.centerX, bounds.centerY);
      this.selectionRect.setSize(bounds.width + 8, bounds.height + 8);
      this.selectionRect.setVisible(true);
    } else if (this.selectionRect) {
      this.selectionRect.setVisible(false);
    }
  }
  
  update(): void {
    this.updateSelectionRect();
  }
  
  clearSelection(): void {
    this.ctx.setSelectedProp(null);
    if (this.selectionRect) {
      this.selectionRect.setVisible(false);
    }
  }
}

